(function (global) {
  'use strict';
  const personalisation = global?.BBC_DIAPER_DASH_PERSONALISATION || (typeof require === 'function' ? require('./bbc-diaper-dash-personalisation.js') : null);
  const GAME_KEY = 'diaper-dash';
  const VERSION = 1;
  const PREFIX = 'bbc-diaper-dash-cloud-v1:';
  const IMPORT_KEY = PREFIX + 'legacy-imported';
  const FIELDS = ['selectedPartnerId', 'soundEffectsEnabled', 'voiceReactionsEnabled'];
  const maxScore = value => Math.min(1000000000, Math.max(0, Number.isSafeInteger(Number(value)) ? Number(value) : 0));
  const freshPatch = () => ({ fields: {}, names: {} });
  const hasPatch = patch => Boolean(Object.keys(patch.fields).length || Object.keys(patch.names).length);
  const copy = value => JSON.parse(JSON.stringify(value));
  const accountKey = id => PREFIX + 'user:' + id;
  const guestKey = () => PREFIX + 'guest';
  const getItem = (storage, key) => { try { return storage?.getItem(key) || null; } catch { return null; } };
  const setItem = (storage, key, value) => { try { if (typeof storage?.setItem !== 'function') return false; storage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } };

  function normalizePatch(value) {
    const result = freshPatch();
    if (!value || typeof value !== 'object') return result;
    for (const field of FIELDS) {
      const item = value.fields?.[field];
      if (field === 'selectedPartnerId' && personalisation.PARTNER_IDS.includes(String(item))) result.fields[field] = String(item);
      if (field !== 'selectedPartnerId' && typeof item === 'boolean') result.fields[field] = item;
    }
    for (const id of personalisation.PARTNER_IDS) {
      const item = value.names?.[id];
      if (item === null) result.names[id] = null;
      else if (typeof item === 'string') {
        const name = personalisation.normalizeName(item);
        if (name) result.names[id] = name;
      }
    }
    return result;
  }

  function changedPatch(before, after, existing = freshPatch()) {
    const patch = normalizePatch(existing);
    for (const field of FIELDS) if (before[field] !== after[field]) patch.fields[field] = after[field];
    for (const id of personalisation.PARTNER_IDS) {
      if ((before.names[id] || '') !== (after.names[id] || '')) patch.names[id] = after.names[id] || null;
    }
    return patch;
  }

  function applyPatch(cloud, patch, base = null, cloudExists = false) {
    const resolved = personalisation.normalizePreferences(cloud);
    const pending = freshPatch();
    const normalizedBase = base ? personalisation.normalizePreferences(base) : null;
    for (const field of FIELDS) {
      if (!Object.hasOwn(patch.fields, field)) continue;
      const conflict = cloudExists && normalizedBase && resolved[field] !== normalizedBase[field];
      if (!conflict) { resolved[field] = patch.fields[field]; pending.fields[field] = patch.fields[field]; }
    }
    for (const id of personalisation.PARTNER_IDS) {
      if (!Object.hasOwn(patch.names, id)) continue;
      const cloudName = resolved.names[id] || '';
      const baseName = normalizedBase?.names[id] || '';
      const conflict = cloudExists && normalizedBase && cloudName !== baseName;
      if (!conflict) {
        if (patch.names[id]) resolved.names[id] = patch.names[id];
        else delete resolved.names[id];
        pending.names[id] = patch.names[id];
      }
    }
    return { preferences: resolved, patch: pending };
  }

  function rowToPreferences(row) {
    if (!row || typeof row !== 'object') return personalisation.defaults();
    return personalisation.normalizePreferences({
      selectedPartnerId: row.selected_partner_id,
      names: row.custom_partner_names,
      soundEffectsEnabled: row.sound_effects_enabled,
      voiceReactionsEnabled: row.voice_reactions_enabled
    });
  }
  function preferencesToRow(preferences) {
    const value = personalisation.normalizePreferences(preferences);
    return {
      selected_partner_id: value.selectedPartnerId,
      custom_partner_names: value.names,
      sound_effects_enabled: value.soundEffectsEnabled,
      voice_reactions_enabled: value.voiceReactionsEnabled
    };
  }
  function readRecord(storage, key, fallbackBest = 0) {
    try {
      const raw = JSON.parse(getItem(storage, key));
      if (raw?.version === VERSION && raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return {
          version: VERSION,
          preferences: personalisation.normalizePreferences(raw.preferences),
          best: Math.max(maxScore(raw.best), fallbackBest),
          patch: normalizePatch(raw.patch),
          basePreferences: raw.basePreferences ? personalisation.normalizePreferences(raw.basePreferences) : null,
          pendingBest: maxScore(raw.pendingBest),
          transferOwnerId: typeof raw.transferOwnerId === 'string' && raw.transferOwnerId ? raw.transferOwnerId : null
        };
      }
    } catch {}
    return { version: VERSION, preferences: personalisation.defaults(), best: fallbackBest, patch: freshPatch(), basePreferences: null, pendingBest: 0, transferOwnerId: null };
  }
  function legacyBest(storage, scope) { return maxScore(getItem(storage, `bbc-diaper-dash-best-v1:${scope}`)); }

  function createCloudSync({ account, accountTarget = null, storage = null, onUpdate = () => {}, onlineTarget = null, isOnline = () => true,
    schedule = setTimeout, cancel = clearTimeout } = {}) {
    let guest = readRecord(storage, guestKey(), legacyBest(storage, 'guest'));
    if (!getItem(storage, guestKey()) && !getItem(storage, IMPORT_KEY)) {
      const legacy = personalisation.readPreferences(storage, 'guest');
      guest.preferences = legacy.preferences;
      guest.patch = changedPatch(personalisation.defaults(), guest.preferences);
      guest.pendingBest = guest.best;
      if (setItem(storage, guestKey(), guest)) setItem(storage, IMPORT_KEY, { version: VERSION });
    }
    let active = guest;
    let userId = null;
    let started = false;
    let stopped = false;
    let unsubscribe = null;
    let connectedAccount = null;
    let connecting = false;
    let epoch = 0;
    let revision = 0;
    let busy = false;
    let rerun = false;
    let timer = null;
    let retries = 0;
    let status = 'saved';
    const persist = () => setItem(storage, userId ? accountKey(userId) : guestKey(), active);
    const emit = () => onUpdate({ userId, preferences: copy(active.preferences), best: active.best, status });
    const setStatus = next => { status = next; emit(); };
    const clearTimer = () => { if (timer !== null) cancel(timer); timer = null; };
    const validUser = (id, ticket) => !stopped && id === userId && ticket === epoch;
    const pending = () => hasPatch(active.patch) || active.pendingBest > 0;

    function queue(delay = 400) {
      if (!userId || stopped) return;
      if (!isOnline()) { setStatus('pending'); return; }
      if (busy) { rerun = true; return; }
      clearTimer();
      timer = schedule(() => { timer = null; void sync(); }, delay);
    }

    async function sync() {
      if (!userId || stopped) return;
      if (busy) { rerun = true; return; }
      if (!isOnline()) { setStatus('pending'); return; }
      clearTimer();
      const id = userId;
      const ticket = epoch;
      busy = true;
      setStatus('saving');
      try {
        const db = connectedAccount.client;
        const [prefResult, statsResult] = await Promise.all([
          db.from('bbc_game_preferences').select('selected_partner_id,custom_partner_names,sound_effects_enabled,voice_reactions_enabled,updated_at').eq('user_id', id).eq('game_key', GAME_KEY).maybeSingle(),
          db.from('bbc_game_statistics').select('personal_best').eq('user_id', id).eq('game_key', GAME_KEY).maybeSingle()
        ]);
        if (!validUser(id, ticket)) return;
        if (prefResult.error || statsResult.error) throw new Error('Cloud read unavailable');
        const cloudRow = prefResult.data;
        const cloudPreferences = rowToPreferences(cloudRow);
        const cloudBest = maxScore(statsResult.data?.personal_best);
        const merged = applyPatch(cloudPreferences, active.patch, active.basePreferences, Boolean(cloudRow));
        active.preferences = merged.preferences;
        active.patch = merged.patch;
        active.best = Math.max(active.best, cloudBest);
        if (active.pendingBest <= cloudBest) active.pendingBest = 0;
        active.pendingBest = Math.max(active.pendingBest, active.best > cloudBest ? active.best : 0);
        persist();
        emit();
        const writtenRevision = revision;
        if (hasPatch(active.patch)) {
          const fields = preferencesToRow(active.preferences);
          let write;
          if (cloudRow) {
            write = await db.from('bbc_game_preferences').update(fields).eq('user_id', id).eq('game_key', GAME_KEY)
              .eq('updated_at', cloudRow.updated_at).select('updated_at').maybeSingle();
          } else {
            write = await db.from('bbc_game_preferences').insert({ user_id: id, game_key: GAME_KEY, ...fields }).select('updated_at').maybeSingle();
          }
          if (!validUser(id, ticket)) return;
          if (write.error || !write.data) throw new Error('Cloud preference write unavailable');
          active.basePreferences = copy(merged.preferences);
          if (writtenRevision === revision) active.patch = freshPatch();
          persist();
        } else { active.basePreferences = copy(cloudPreferences); persist(); }
        if (active.pendingBest > cloudBest) {
          const score = active.pendingBest;
          const result = await db.rpc('bbc_game_record_best', { p_user_id: id, p_game_key: GAME_KEY, p_score: score });
          if (!validUser(id, ticket)) return;
          if (result.error || !Number.isFinite(Number(result.data))) throw new Error('Cloud score write unavailable');
          active.best = Math.max(active.best, maxScore(result.data));
          if (active.pendingBest <= active.best && writtenRevision === revision) active.pendingBest = 0;
          persist();
        }
        retries = 0;
        if (!pending() && guest.transferOwnerId === id) {
          const cleared = { ...guest, patch: freshPatch(), pendingBest: 0, transferOwnerId: null };
          if (setItem(storage, guestKey(), cleared)) guest = cleared;
        }
        setStatus(pending() ? 'pending' : 'saved');
      } catch {
        if (!validUser(id, ticket)) return;
        persist();
        setStatus('pending');
        if (retries < 4 && isOnline()) {
          retries += 1;
          clearTimer();
          timer = schedule(() => { timer = null; void sync(); }, Math.min(8000, 1000 * 2 ** (retries - 1)));
        }
      } finally {
        busy = false;
        if (rerun && !stopped) { rerun = false; queue(0); }
      }
    }

    function switchUser(user) {
      const nextId = typeof user?.id === 'string' && user.id ? user.id : null;
      if (nextId === userId) return;
      clearTimer();
      epoch += 1;
      retries = 0;
      rerun = false;
      userId = nextId;
      if (!userId) { active = guest; setStatus('saved'); return; }
      active = readRecord(storage, accountKey(userId), legacyBest(storage, `user:${userId}`));
      if ((hasPatch(guest.patch) || guest.pendingBest) && (!guest.transferOwnerId || guest.transferOwnerId === userId)) {
        if (!guest.transferOwnerId) {
          guest.transferOwnerId = userId;
          if (!setItem(storage, guestKey(), guest)) { setStatus('pending'); return; }
        }
        active.patch = normalizePatch({
          fields: { ...guest.patch.fields, ...active.patch.fields },
          names: { ...guest.patch.names, ...active.patch.names }
        });
        active.best = Math.max(active.best, guest.best);
        active.pendingBest = Math.max(active.pendingBest, guest.pendingBest);
        if (setItem(storage, accountKey(userId), active)) {
          const cleared = { ...guest, patch: freshPatch(), pendingBest: 0, transferOwnerId: null };
          if (setItem(storage, guestKey(), cleared)) guest = cleared;
        }
      }
      setStatus('saving');
      queue(0);
    }

    function changePreferences(value) {
      const next = personalisation.normalizePreferences(value);
      active.patch = changedPatch(active.preferences, next, active.patch);
      active.preferences = next;
      revision += 1;
      persist();
      if (userId) { setStatus('saving'); queue(); }
      else emit();
    }
    function recordBest(value) {
      const score = maxScore(value);
      if (score <= active.best) return;
      active.best = score;
      active.pendingBest = Math.max(active.pendingBest, score);
      revision += 1;
      persist();
      if (userId) { setStatus('saving'); queue(); }
      else emit();
    }
    function onOnline() { retries = 0; if (userId) queue(0); }
    async function connectAccount() {
      if (stopped || unsubscribe || connecting) return;
      const candidate = typeof account === 'function' ? account() : account;
      if (!candidate?.ready || typeof candidate.subscribe !== 'function' || !candidate.client) return;
      connecting = true;
      try {
        try { await candidate.ready; } catch {}
        if (stopped || unsubscribe) return;
        connectedAccount = candidate;
        unsubscribe = candidate.subscribe(switchUser);
        switchUser(candidate.getUser?.());
      } finally { connecting = false; }
    }
    async function start() {
      if (started || stopped) return;
      started = true;
      emit();
      onlineTarget?.addEventListener?.('online', onOnline);
      accountTarget?.addEventListener?.('bbc-account-ready', connectAccount);
      await connectAccount();
    }
    function stop() {
      if (stopped) return;
      stopped = true;
      epoch += 1;
      clearTimer();
      unsubscribe?.();
      accountTarget?.removeEventListener?.('bbc-account-ready', connectAccount);
      onlineTarget?.removeEventListener?.('online', onOnline);
    }
    return Object.freeze({ start, stop, changePreferences, recordBest, flush: sync, getSnapshot: () => ({ userId, preferences: copy(active.preferences), best: active.best, status }), retry: onOnline });
  }

  function createLocalState({ storage = null, onUpdate = () => {} } = {}) {
    const localBestKey = 'bbc-diaper-dash-best-v1:guest';
    const localPreferences = personalisation.readPreferences(storage);
    const hasGuestRecord = Boolean(getItem(storage, guestKey()));
    const guest = readRecord(storage, guestKey(), legacyBest(storage, 'guest'));
    let preferences = localPreferences.source === 'v2'
      ? localPreferences.preferences
      : hasGuestRecord ? guest.preferences : localPreferences.preferences;
    let best = Math.max(legacyBest(storage, 'guest'), guest.best);
    let stopped = false;
    const emit = () => { if (!stopped) onUpdate({ userId: null, preferences: copy(preferences), best, status: 'saved' }); };
    const save = () => {
      personalisation.savePreferences(storage, preferences);
      guest.preferences = copy(preferences);
      guest.best = Math.max(guest.best, best);
      guest.patch = changedPatch(personalisation.defaults(), preferences, guest.patch);
      guest.pendingBest = Math.max(guest.pendingBest, best);
      setItem(storage, guestKey(), guest);
      try { storage?.setItem(localBestKey, String(best)); } catch {}
    };
    best = Math.max(best, maxScore(getItem(storage, localBestKey)));
    if (localPreferences.source !== 'v2' && hasGuestRecord) personalisation.savePreferences(storage, preferences);
    function start() { emit(); }
    function stop() { stopped = true; }
    function changePreferences(value) {
      preferences = personalisation.normalizePreferences(value);
      save();
      emit();
    }
    function recordBest(value) {
      const score = maxScore(value);
      if (score <= best) return;
      best = score;
      save();
      emit();
    }
    return Object.freeze({ start, stop, changePreferences, recordBest, flush: async () => {}, getSnapshot: () => ({ userId: null, preferences: copy(preferences), best, status: 'saved' }) });
  }

  const api = Object.freeze({ GAME_KEY, VERSION, createCloudSync, createLocalState, rowToPreferences, preferencesToRow, normalizePatch, changedPatch, applyPatch });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global) global.BBC_DIAPER_DASH_CLOUD = api;
})(typeof window !== 'undefined' ? window : null);
